import { useCallback, useEffect, useState } from 'react';
import { US_STATES_DATA, slugifyState, getStateInfoByKey } from './usStates';

// Owns the user's saved location.
//
// Two keys are written, deliberately:
//   homeState    - the existing slug ("indiana"). Onboarding, LocationSettingsPage
//                  and IntegratedSearchNavBar all read it, so it stays exactly as
//                  it was rather than being migrated.
//   homeLocation - the richer object this module adds, holding the address, city,
//                  county, two-letter abbreviation and resolved districts that
//                  api2.govglance.org needs.
//
// homeState remains the source of truth for the state itself; homeLocation is
// rewritten from it whenever the two disagree, so an older screen writing only
// homeState never leaves a stale city attached to a different state.
//
// The stored districts are the point of this record. Once they are here, the
// Representatives page fetches from /districts/officials using the numbers
// alone, and the street address never leaves the device again.

const LOCATION_KEY = 'homeLocation';
const LEGACY_STATE_KEY = 'homeState';

// localStorage fires 'storage' only in *other* tabs. This event covers the
// current one so a save in the picker re-renders the page behind it.
const CHANGE_EVENT = 'homelocation:change';

// How precisely the location is known. Drives which endpoint gets called and
// whether district-level answers are trustworthy.
//
// DISTRICT is the one that matters: districts can now be chosen by hand from
// /districts/state/{state}, so someone who will not type a street address can
// still get their exact seats. Anything gated on "do we know the districts"
// must test hasDistricts(), not precision === ADDRESS.
export const PRECISION = {
  ADDRESS: 'address',   // districts resolved from a street address
  DISTRICT: 'district', // districts known, no address held
  CITY: 'city',         // local officials, no district accuracy
  STATE: 'state',       // delegation and legislature only
  NONE: 'none',
};

export const hasDistricts = (location) =>
  Boolean(
    location &&
      (location.congressionalDistrict || location.stateUpperDistrict || location.stateLowerDistrict)
  );

// ─── Abbreviations ────────────────────────────────────────────────────────────
// api2 speaks two-letter USPS codes; the app stores slugs. Both directions are
// needed -- slug to code for the request, code to slug when the geocoder answers
// with "IN" -- and this module owns both rather than depending on
// US_STATES_DATA carrying an `abbr` field.
const ABBR_BY_NAME = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS',
  Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
  Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI',
  Wyoming: 'WY',
  'District of Columbia': 'DC',
  'Puerto Rico': 'PR', Guam: 'GU', 'American Samoa': 'AS',
  'U.S. Virgin Islands': 'VI', 'Virgin Islands': 'VI',
  'Northern Mariana Islands': 'MP',
};

// Reduces both sides to lowercase letters so the lookup survives whatever
// slugifyState does with spaces, periods and hyphens.
const normalizeKey = (value) => String(value || '').toLowerCase().replace(/[^a-z]/g, '');

const ABBR_BY_SLUG = Object.entries(ABBR_BY_NAME).reduce((acc, [name, abbr]) => {
  acc[normalizeKey(name)] = abbr;
  return acc;
}, {});

export const abbrForStateKey = (stateKey) => {
  if (!stateKey) return null;
  const info = getStateInfoByKey(stateKey);
  // Prefer the data file if it happens to carry one.
  if (info?.abbr) return info.abbr;
  return ABBR_BY_SLUG[normalizeKey(stateKey)] || ABBR_BY_SLUG[normalizeKey(info?.name)] || null;
};

// Built from US_STATES_DATA so the picker lists exactly the states the app
// supports, with the abbreviation resolved through the map above. `flag` comes
// straight from the data file and is null for places with no asset.
export const STATE_OPTIONS = US_STATES_DATA.map((state) => ({
  key: state.key,
  name: state.name,
  abbr: abbrForStateKey(state.key),
  flag: state.flag || null,
}));

// The reverse lookup. Previously the picker searched STATE_OPTIONS for a
// matching abbr, which silently failed with "that state is not supported yet"
// whenever the options carried no abbreviation. Resolving against the map first
// removes that dependency.
export const stateKeyForAbbr = (abbr) => {
  if (!abbr) return null;
  const wanted = String(abbr).trim().toUpperCase();

  const fromOptions = STATE_OPTIONS.find((option) => option.abbr === wanted);
  if (fromOptions) return fromOptions.key;

  // The app's state list does not include this place (territories, most likely).
  const name = Object.keys(ABBR_BY_NAME).find((key) => ABBR_BY_NAME[key] === wanted);
  return name ? slugifyState(name) : null;
};

// ─── Storage ──────────────────────────────────────────────────────────────────

const readRaw = () => {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Corrupt or unparseable: treat as unset rather than throwing on every render.
    return null;
  }
};

// Every district field defaults to null here so a record written before the
// /districts migration still spreads cleanly over a complete shape.
const emptyDistricts = {
  congressionalDistrict: null,
  congressionalLabel: null,
  stateUpperDistrict: null,
  stateUpperLabel: null,
  stateUpperChamberName: null,
  stateLowerDistrict: null,
  stateLowerLabel: null,
  stateLowerChamberName: null,
};

export const readHomeLocation = () => {
  const stateKey = localStorage.getItem(LEGACY_STATE_KEY);
  const stored = readRaw();

  if (!stateKey) return null;

  const info = getStateInfoByKey(stateKey);
  const stateAbbr = abbrForStateKey(stateKey);

  // A slug the app does not recognize at all is the one case worth giving up on.
  if (!info && !stateAbbr) return null;

  const base = {
    stateKey,
    stateName: info?.name || null,
    stateAbbr,
    stateFlag: info?.flag || null,
    address: null,
    city: null,
    county: null,
    countyGeoid: null,
    geoid: null,
    ...emptyDistricts,
    precision: PRECISION.STATE,
  };

  // Another screen changed the state without going through here, so anything
  // narrower than the state is no longer about this place. Districts especially:
  // district 14 in the old state is a different place entirely in the new one.
  if (!stored || stored.stateKey !== stateKey) return base;

  // stateName and stateAbbr are always recomputed rather than trusted from
  // storage, so a record written before this map existed still resolves.
  return {
    ...base,
    ...stored,
    stateKey,
    stateName: base.stateName,
    stateAbbr: base.stateAbbr,
    stateFlag: base.stateFlag,
  };
};

const precisionFor = (record) => {
  if (record.address) return PRECISION.ADDRESS;
  if (hasDistricts(record)) return PRECISION.DISTRICT;
  if (record.city) return PRECISION.CITY;
  return PRECISION.STATE;
};

export const writeHomeLocation = (location) => {
  if (!location?.stateKey) return null;

  const record = {
    stateKey: location.stateKey,
    stateAbbr: location.stateAbbr || abbrForStateKey(location.stateKey),
    address: location.address || null,
    city: location.city || null,

    // The county name is what /elections/candidates filters on and the GEOID is
    // what /officials filters on, so both are held. Neither is ever typed --
    // they come back from /districts and from /officials/directory.
    county: location.county || null,
    countyGeoid: location.countyGeoid || null,
    geoid: location.geoid || null,

    congressionalDistrict: location.congressionalDistrict || null,
    congressionalLabel: location.congressionalLabel || null,

    stateUpperDistrict: location.stateUpperDistrict || null,
    stateUpperLabel: location.stateUpperLabel || null,
    stateUpperChamberName: location.stateUpperChamberName || null,

    stateLowerDistrict: location.stateLowerDistrict || null,
    stateLowerLabel: location.stateLowerLabel || null,
    stateLowerChamberName: location.stateLowerChamberName || null,

    updatedAt: new Date().toISOString(),
  };

  record.precision = precisionFor(record);

  localStorage.setItem(LEGACY_STATE_KEY, record.stateKey);
  localStorage.setItem(LOCATION_KEY, JSON.stringify(record));
  window.dispatchEvent(new Event(CHANGE_EVENT));

  return readHomeLocation();
};

// Fills in fields the user never typed, without disturbing anything they did.
//
// /officials/directory echoes the county and its GEOID on every response, so the
// page that already has that answer can keep it rather than the app asking for
// it again later. Existing values always win: this only ever fills a blank, so
// it can be called from a fetch effect without a save racing an edit.
//
// Returns null when there is nothing new, so a caller can skip the write and
// the re-render that follows it.
export const mergeHomeLocation = (patch) => {
  const current = readHomeLocation();
  if (!current || !patch) return null;

  const additions = Object.entries(patch).filter(
    ([field, value]) => value && !current[field]
  );
  if (!additions.length) return null;

  const next = { ...current };
  additions.forEach(([field, value]) => {
    next[field] = value;
  });

  return writeHomeLocation(next);
};

// Drops the street address and keeps the districts it resolved. This is the
// honest version of "stop holding my address": the address was only ever a means
// of finding the districts, and the districts are what the page actually queries
// with, so removing it costs the user nothing.
export const clearStoredAddress = () => {
  const current = readHomeLocation();
  if (!current) return null;
  return writeHomeLocation({ ...current, address: null });
};

// Clears the address, city and districts but keeps the state, which is what
// "forget where I live" means. Removing the state would also log the user out of
// every other screen keyed on homeState.
export const clearPreciseLocation = () => {
  const current = readHomeLocation();
  if (!current) return null;
  return writeHomeLocation({ stateKey: current.stateKey, stateAbbr: current.stateAbbr });
};

export const useHomeLocation = () => {
  const [location, setLocation] = useState(readHomeLocation);

  useEffect(() => {
    const sync = () => setLocation(readHomeLocation());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const save = useCallback((next) => setLocation(writeHomeLocation(next)), []);
  const clearPrecise = useCallback(() => setLocation(clearPreciseLocation()), []);
  const clearAddress = useCallback(() => setLocation(clearStoredAddress()), []);

  // Stable across renders, which matters: RepresentativesPage lists this in the
  // dependency array of the effect that fetches, so a fresh function each render
  // would refetch in a loop.
  const merge = useCallback((patch) => {
    const next = mergeHomeLocation(patch);
    if (next) setLocation(next);
  }, []);

  return {
    location,
    saveLocation: save,
    mergeLocation: merge,
    clearPreciseLocation: clearPrecise,
    clearStoredAddress: clearAddress,
  };
};

// ─── Display ──────────────────────────────────────────────────────────────────

// Human-readable label for the header: "Carmel, IN" / "Indiana".
export const describeLocation = (location) => {
  if (!location) return '';
  if (location.city && location.stateAbbr) return `${location.city}, ${location.stateAbbr}`;
  return location.stateName || location.stateAbbr || '';
};

// The saved districts as the state itself names them, for the location button
// and the settings screen. Falls back to a plain number when no label was
// stored, which is what a hand-picked district without a label looks like.
export const describeDistricts = (location) => {
  if (!location) return [];

  const entries = [
    {
      key: 'congressional',
      label: location.congressionalLabel,
      district: location.congressionalDistrict,
      chamberName: null,
    },
    {
      key: 'upper',
      label: location.stateUpperLabel,
      district: location.stateUpperDistrict,
      chamberName: location.stateUpperChamberName,
    },
    {
      key: 'lower',
      label: location.stateLowerLabel,
      district: location.stateLowerDistrict,
      chamberName: location.stateLowerChamberName,
    },
  ];

  return entries.filter((entry) => entry.district || entry.label);
};

// api2 takes a county with or without the noun -- "Wayne" and "Wayne County"
// are the same query -- but the states do not agree with each other about which
// they publish. Stripping it gives one spelling to compare against, so a county
// echoed by one endpoint matches the same county listed by another.
export const bareCountyName = (county) =>
  String(county || '')
    .replace(/\s+(county|parish|borough|census area|municipality)$/i, '')
    .trim();

export { slugifyState };